"use client"

import Link from "next/link"
import Image from "next/image"
import { ShareButton } from "@/components/pages/changelog/ShareButton"
import GitHubIcon from "@/components/core/icons/GitHubIcon"
import { useState, useEffect, Fragment } from "react"
import clsx from "clsx"
import type { ChangelogPreview } from "./types"
import { FormattedDate } from "@/components/ui/formatted-date"

export default function ChangelogsClient({
  logs,
  bannerActive,
}: {
  logs: ChangelogPreview[]
  bannerActive: boolean
}) {
  const [visibleTag, setVisibleTag] = useState<string>(
    logs[0] ? "tag-" + logs[0].slug : "",
  )

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const tagId = entry.target.id.replace("changelog-", "tag-")
            setVisibleTag(tagId)
          }
        })
      },
      {
        root: null,
        rootMargin: "0px 0px -70% 0px",
        threshold: 0.5,
      }
    )

    const logElements = document.querySelectorAll('[id^="changelog-"]')
    logElements.forEach((log) => {
      observer.observe(log)
    })

    return () => {
      logElements.forEach((log) => {
        observer.unobserve(log)
      })
    }
  }, [logs])

  return (
    <div className="w-full mt-20 col-span-9 grid grid-cols-5 gap-4 lg:col-start-2">
      {logs.map((log, i) => (
        <Fragment key={log.slug + i}>
          <div
            className="flex col-span-full md:col-span-1"
          >
            {logs.indexOf(log) !== logs.length - 1 && (
              <div className="w-px bg-grey-850 h-[calc(100%+16px)] relative left-4 hidden md:block overflow-hidden">
                <div
                  className={clsx(
                    "h-1/3 bg-linear-to-b from-transparent via-red-500 to-transparent absolute top-0 w-px",
                    (logs.indexOf(log) % 2 === 0
                      ? "animate-move-down-2"
                      : "animate-move-down")
                  )}
                />
              </div>
            )}
            <p
              id={"tag-" + log.slug}
              key={log.slug + "2"}
              className={clsx(
                "text-[9px] m-0 py-1 font-semibold uppercase px-2 border z-10 font-mono transition-all duration-200 rounded self-start",
                "md:sticky",
                bannerActive ? "top-32 " : "top-24 ",
                (logs.indexOf(log) !== logs.length - 1
                  ? "md:mb-20 "
                  : "mb-0 "),
                (visibleTag === "tag-"+ log.slug
                  ? "text-white bg-red-900 border-red-800"
                  : "bg-grey-950 border-grey-850")
              )}
            >
              <FormattedDate date={log.date} compact />
            </p>
          </div>
          <article
            key={log.slug}
            className={clsx(
              "col-span-full md:col-span-4 bg-[#1D1D1D] p-8 md:ml-6 rounded",
              (logs.indexOf(log) !== logs.length - 1 ? "mb-20" : "mb-0")
            )}
          >
            <h2
              id={"changelog-"+ log.slug}
              className="m-0 text-[32px] leading-none mb-4 font-normal text-balance"
            >
              <Link href={"/changelog/" + log.slug}>{log.title}</Link>
            </h2>
            <p className="m-0 text-[17px] font-medium text-balance mb-6">
              {log.description}
            </p>
            <div
              id="log-preview"
              className="mb-6 max-h-[550px] text-[17px] overflow-hidden text-grey-400 font-medium text-balance relative pb-10"
            >
              {log.body}
              <div className="absolute bottom-0 left-0 w-full h-20 bg-linear-to-t from-[#1D1D1D] via-[#1D1D1D]/90 to-transparent pointer-events-none" />
            </div>
            <Link
              href={"/changelog/" + log.slug}
              className="text-sm font-medium text-grey-400 bg-grey-850 hover:bg-grey-800 hover:text-white rounded transition-all duration-200 px-2 py-1"
            >
              Read more
            </Link>
            <div className="pt-6 flex flex-col gap-8 mt-10 border-t border-grey-850">
              <div className="flex gap-2 flex-wrap">
                {log.author.map((contributor) =>
                  contributor.includeInAuthorsListing && contributor.slug ? (
                    <Link
                      key={contributor.name}
                      href={`/authors/${contributor.slug}`}
                      className="flex gap-0.5 justify-center items-center group"
                    >
                    {contributor.imageUrl &&
                      <Image
                        src={contributor.imageUrl}
                        alt={contributor.name}
                        width={24}
                        height={24}
                        className="w-6 h-6 rounded-sm"
                      />
                    }
                      <p className="m-0 tracking-wider text-[9px] h-6 px-2 bg-grey-850 group-hover:bg-grey-800 transition-all duration-200 flex items-center font-semibold uppercase font-mono text-balance">
                        {contributor.name}
                      </p>
                    </Link>
                  ) : contributor.linkedinUrl ? (
                    <Link
                      key={contributor.name}
                      target="_blank"
                      href={contributor.linkedinUrl || "/"}
                      className="flex gap-0.5 justify-center items-center group"
                    >
                    {contributor.imageUrl &&
                      <Image
                        src={contributor.imageUrl}
                        alt={contributor.name}
                        width={24}
                        height={24}
                        className="w-6 h-6 rounded-sm"
                      />
                    }
                      <p className="m-0 tracking-wider text-[9px] h-6 px-2 bg-grey-850 group-hover:bg-grey-800 transition-all duration-200 flex items-center font-semibold uppercase font-mono text-balance">
                        {contributor.name}
                      </p>
                    </Link>
                  ) : (
                    <div
                      key={contributor.name}
                      className="flex gap-0.5 justify-center items-center"
                    >
                      {contributor.imageUrl &&
                        <Image
                          src={contributor.imageUrl}
                          alt={contributor.name}
                          width={24}
                          height={24}
                          className="w-6 h-6 rounded-sm"
                        />
                      }
                      <p className="m-0 tracking-wider text-[9px] h-6 px-2 bg-grey-850 flex items-center font-semibold uppercase font-mono text-balance">
                        {contributor.name}
                      </p>
                    </div>
                  )
                )}
              </div>
              <div className="flex gap-8">
                <ShareButton
                  shareLink={"https://dash0.com/changelog/" + log.slug}
                />
                {log.github && (
                  <Link
                    href={log.github}
                    target="_blank"
                    className="flex gap-2 items-center font-medium text-grey-600 hover:text-white transition-all duration-200"
                  >
                    <GitHubIcon />
                    View on GitHub
                  </Link>
                )}
              </div>
            </div>
          </article>
        </Fragment>
      ))}
    </div>
  )
}
